import { Stack } from '@mui/material';
import { ChatBox } from '@rumblefish/ui/Rumblefish23Theme';
import type { Message } from '@rumblefish/ui/src/components/Rumblefish23Theme/ChatBox/ChatBox.types';
import { StyledChatWrapper } from '../../aiChatServicePage/ChatUseCases/styles';
import { CaseStudyBentoGrid } from '../BentoGrid/BentoGrid';
import { CaseStudyComponentSlot } from '../ComponentSlot/ComponentSlot';
import { CaseStudyCustomSection } from '../CustomSection/CustomSection';
import { CaseStudyDetailsStack } from '../DetailsStack/DetailsStack';
import { CaseStudyImagesCarousel } from '../ImagesCarousel/ImagesCarousel';
import { CaseStudySolutions } from '../Solutions/Solutions';
import { CaseStudyTestimonial } from '../Testimonial/Testimonial';
import { StyledSection, StyledContent } from './Content.styles';
import type { ContentProps } from './Content.types';
import { VideoComponent } from '../Video';

export const Content = ({ components, caseStudy }: ContentProps) => {
  return (
    <StyledSection width="normal">
      <StyledContent>
        {components?.map((component, index) => {
          switch (component.__typename) {
            case 'ComponentGroupBentoGrid':
              return (
                <CaseStudyBentoGrid
                  key={index}
                  items={component.items}
                  columns={component.columns}
                  rows={component.rows}
                  gap={component.gap}
                />
              );
            case 'ComponentSectionCustomSection':
              return (
                <CaseStudyCustomSection
                  key={index}
                  title={component.title}
                  content={component.content}
                  customTitle={component.customTitle}
                />
              );
            case 'ComponentSingleDetailsStack':
              return (
                <CaseStudyDetailsStack
                  key={index}
                  collaborationTimeframe={caseStudy?.collaborationTimeframe}
                  technologies={caseStudy?.case_study_technologies
                    .map((technology) => technology?.name)
                    .join(', ')}
                  services={caseStudy?.case_study_services
                    .map((service) => service?.name)
                    .join(', ')}
                  teamSize={caseStudy?.teamSize}
                  show={component.show}
                />
              );
            case 'ComponentGroupComponentSlot':
              return (
                <CaseStudyComponentSlot
                  key={index}
                  componentType={component.componentType}
                  props={component.props}
                />
              );
            case 'ComponentSingleTestimonial':
              return (
                <>
                  {caseStudy?.case_study_testimonials?.map(
                    (testimonial, index) => (
                      <CaseStudyTestimonial
                        key={index}
                        show={component.show}
                        name={testimonial?.author?.Name}
                        positionInCompany={testimonial?.author?.Position}
                        text={testimonial?.content}
                        avatarSrc={testimonial?.author?.Avatar?.url}
                      />
                    )
                  )}
                </>
              );

            case 'ComponentSingleChatbotMessages': {
              return (
                <Stack sx={{ width: '100%' }} alignItems="center" key={index}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    sx={{ maxWidth: '800px', width: '100%' }}
                    justifyContent={'space-between'}
                    spacing={4}>
                    {component.messageGroups?.map((messageGroup, idx) => (
                      <StyledChatWrapper key={idx}>
                        <ChatBox
                          messages={(messageGroup?.messages ?? []) as Message[]}
                        />
                      </StyledChatWrapper>
                    ))}
                  </Stack>
                </Stack>
              );
            }

            case 'ComponentSingleImagesCarousel': {
              const componentImages = component.images;

              const imagesCarousel =
                componentImages
                  ?.filter((item) => item?.url != caseStudy?.image?.url)
                  .map((img) => ({
                    img: img?.url || '',
                    alt: img?.alternativeText || '',
                  })) || [];

              return <CaseStudyImagesCarousel images={imagesCarousel} />;
            }

            case 'ComponentGroupSolutionSection': {
              return (
                <CaseStudySolutions
                  key={index}
                  solution={{
                    title: 'The solution',
                    description: {
                      firstParagraph: component.firstRow,
                      secondParagraph: component.secondRowLeftColumn ?? '',
                      thirdParagraph: component.secondRowRightColumn ?? '',
                    },
                  }}
                />
              );
            }

            case 'ComponentSingleVideo': {
              return (
                <VideoComponent
                  url={component.video?.url}
                  alternativeText={component.video?.alternativeText}
                  mime={component.video?.mime}
                />
              );
            }

            default:
              return null;
          }
        }) || []}
      </StyledContent>
    </StyledSection>
  );
};
